import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { AUTH_CONFIG, AuthManager } from './auth.config';
import { syncLogoutToVueApp } from './cross-origin-sync';
import { APP_ROUTES } from '../constants/routes';
import { EMULATION_FLAG, STORAGE_KEYS } from '../constants/storageKeys';
import { HTTP_REQUEST_TIMEOUT_MS } from '../constants/timing';
import { recordServerDate } from '../lib/serverClock';
import { clearAppCaches } from '../utils/clearAppCaches';
import { reportApiFailureToAnalytics } from '../utils/bugLogger';
import { logger } from '../utils/logger';

/**
 * Faithful port of `RW-ERP-Service/src/utils/rw.axios.ts` —
 *
 *   • Request interceptor:  attaches the bearer token to every request
 *                           EXCEPT `/auth/*` calls (login / refresh).
 *   • Response interceptor: on 401 (or an axios error with no response
 *                           object — some 401s ship that way), refreshes
 *                           the access + refresh tokens, writes both to
 *                           localStorage, and replays the original
 *                           request. `_retry` prevents a re-loop.
 *   • Refresh failure:      401 → log out & redirect to Vue login.
 *                           403 / 5xx / network → propagate, KEEP session.
 *
 * One deviation from the Vue source: concurrent 401s share a single
 * in-flight refresh promise instead of each firing their own
 * `/auth/refreshtoken` call. The Vue implementation fires N refreshes
 * for N concurrent 401s — wasteful on a real page with many requests
 * in flight. The dedup keeps the wire behaviour from Vue's perspective
 * (one refresh, one new token pair) without changing any other
 * semantics.
 */

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_INTERNAL_SERVER_ERROR = 500;
const HTTP_CLIENT_ERROR_THRESHOLD = 400;
const AUTH_URL_FRAGMENT = '/auth/';

/**
 * Emulation request headers — spelled EXACTLY as the Vue ERP sends them
 * (`RW-ERP-Service/src/utils/rw.axios.ts:35-49`), because the backend matches
 * on these names:
 *
 *   emulationMode   'true'
 *   emulatedUserId  <uuid>
 *
 * The ERP's own comment there records the contract: "if emulated user and draft
 * products then, pass the headers — 1) emulationMode true/false
 * 2) emulatedUserId null/uuid". Endpoints that resolve the acting user from
 * these headers (rather than from a userId in the path or body) had no way to
 * know React was emulating, so every such call ran as the REAL user while the
 * UI showed the emulated one.
 */
const EMULATION_MODE_HEADER = 'emulationMode';
const EMULATED_USER_ID_HEADER = 'emulatedUserId';

interface RetriableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const apiClient = axios.create({
  baseURL: AUTH_CONFIG.API_BASE_URL,
  timeout: HTTP_REQUEST_TIMEOUT_MS,
});

/**
 * Attach the emulation headers when — and only when — an emulation is active.
 *
 * Two deliberate differences from the ERP, which sets these as axios INSTANCE
 * DEFAULTS computed once at module import:
 *
 *  1. Read PER REQUEST, not at import. The ERP's headers are frozen for the
 *     page's lifetime, which is precisely why Vue force-reloads the page when an
 *     emulation starts or ends (`UserEmulation.vue`'s `pageReload()`). Reading
 *     here means a request issued after `beginEmulation()` / `endEmulation()`
 *     carries the right headers whether or not a reload happened — and React
 *     does have paths that change the flag without one (the cross-origin probe
 *     in `AuthContext` mirrors a Vue-side emulation into this origin).
 *
 *  2. Test the flag for `=== 'true'`, never `!== 'false'`. The ERP branches on
 *     `isEmulation == 'false'` to mean "not emulating", so an ABSENT key
 *     (`null == 'false'` is false) falls through to the emulating branch and
 *     ships `emulationMode: null, emulatedUserId: ''`. That is the same
 *     inverted-flag bug fixed in Vue's `permission-checker.ts`; `rw.axios.ts`
 *     still has it. Here an absent or `'false'` flag sends NEITHER header, so
 *     the request is indistinguishable from a normal one.
 *
 * A missing `emulatedUserId` also suppresses both headers: `emulationMode: true`
 * with no id tells the backend to emulate nobody, which is worse than not
 * claiming to emulate at all.
 */
function applyEmulationHeaders(config: InternalAxiosRequestConfig): void {
  if (typeof window === 'undefined') return;
  try {
    const flag = window.localStorage.getItem(STORAGE_KEYS.isEmulate);
    if (flag !== EMULATION_FLAG.ON) return;
    const emulatedUserId = window.localStorage.getItem(
      STORAGE_KEYS.emulatedUserId,
    );
    if (!emulatedUserId) return;
    config.headers[EMULATION_MODE_HEADER] = EMULATION_FLAG.ON;
    config.headers[EMULATED_USER_ID_HEADER] = emulatedUserId;
  } catch {
    // localStorage unavailable (private-mode quota, disabled storage) — send
    // the request without emulation headers rather than failing it outright.
  }
}

apiClient.interceptors.request.use(
  (config) => {
    // Mirrors Vue rw.axios.ts:63-66 — `/auth/*` endpoints are pre-auth
    // (login, refresh, google SSO) and the backend rejects them when a
    // stale bearer is attached. Every other request gets the current
    // access token read FRESH from localStorage so a post-refresh
    // request picks up the rotated token automatically.
    const url = config.url ?? '';
    if (!url.includes(AUTH_URL_FRAGMENT)) {
      const token = AuthManager.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      applyEmulationHeaders(config);
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

/**
 * Full local-session teardown + redirect to THIS app's login page. Mirrors
 * Vue's `authStore.logout()` call in `rw.axios.ts:125` for a dead refresh
 * token: clears local auth state (full localStorage/sessionStorage wipe),
 * broadcasts the logout to other same-origin tabs, awaits a wipe of
 * persistent app caches (every IndexedDB database on this origin),
 * best-effort syncs the logout to the Vue app so its own session is
 * cleared too, then navigates to `APP_ROUTES.login`. `clearAppCaches()` is
 * awaited (not fire-and-forget) because the `window.location.href`
 * assignment below unloads this page — an un-awaited async IndexedDB
 * delete could be cut off mid-flight by the navigation.
 *
 * Destination is React's own `/login`, NOT `externalVueLoginUrl()`. Mirroring
 * Vue's logout means "go to my own login", the way Vue goes to Vue's — pointing
 * at the Vue origin bounced the user into the other app and changed the origin
 * in the address bar. Same reasoning as `handleExternalLogout` in
 * `contexts/AuthContext.tsx`; these were the two sites that did it.
 */
async function forceLogoutToLogin(): Promise<void> {
  AuthManager.clearAuth();
  AuthManager.broadcastAuthChange('logout');
  await clearAppCaches();
  try {
    await syncLogoutToVueApp();
  } catch (error) {
    logger.warn('Vue logout sync failed (non-fatal):', error);
  }
  window.location.href = APP_ROUTES.login;
}

/**
 * Shared in-flight refresh promise. The FIRST 401 in a batch starts
 * the refresh; every subsequent 401 awaits the same promise. Cleared
 * as soon as the refresh settles (success OR failure) so the next
 * round of 401s can trigger a fresh refresh.
 *
 * This is the ONE place we deviate from the Vue source — Vue fires
 * one refresh per failing request and gets the continuous-refresh
 * spam we're explicitly avoiding here.
 */
let inFlightRefresh: Promise<void> | null = null;

/**
 * Refresh the access token. Faithful to Vue's `rw.axios.ts:108-158`:
 *
 *   1. Read the stored refresh token.
 *   2. POST `/auth/refreshtoken` with `{ refreshToken }` via the RAW
 *      axios (NOT `apiClient`) so this call never re-enters the
 *      response interceptor.
 *   3. Pull `accessToken` + `refreshToken` from
 *      `response.data.data.AuthorizationDetails` and write BOTH to
 *      localStorage. This is the React equivalent of Vue's
 *      `authStore.updateToken(newAccess, newRefresh)` —
 *      `auth.store.ts:178-185`.
 *   4. On failure:
 *        - 401 → the refresh token itself is dead; no further retry can
 *          recover it, so this logs the user out and redirects to THIS
 *          app's login page (`forceLogoutToLogin`), matching Vue's
 *          `authStore.logout()` call at `rw.axios.ts:125`.
 *        - 403 / 5xx / network / other → log and propagate the error to
 *          the caller. The session is kept intact; the next API call
 *          will trigger another refresh attempt, giving the backend a
 *          chance to recover (e.g. a brief outage) without booting the
 *          user for a non-terminal failure.
 *      The error is RE-THROWN so the caller (the response interceptor)
 *      can decide what to do with the original request.
 */
export async function refreshAccessToken(): Promise<void> {
  try {
    const refreshToken = AuthManager.getRefreshToken();
    const response = await axios.post(
      `${AUTH_CONFIG.API_BASE_URL}${AUTH_CONFIG.REFRESH_ENDPOINT}`,
      { refreshToken },
    );
    const newAccessToken =
      response.data?.data?.AuthorizationDetails?.accessToken;
    const newRefreshToken =
      response.data?.data?.AuthorizationDetails?.refreshToken;
    if (!newAccessToken || !newRefreshToken) {
      throw new Error('Refresh response missing AuthorizationDetails tokens');
    }
    // updateToken-equivalent — single write site for both keys so a
    // partial update can never happen.
    AuthManager.setToken(newAccessToken);
    AuthManager.setRefreshToken(newRefreshToken);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;
      if (status === HTTP_UNAUTHORIZED) {
        // Refresh token itself is dead — no further retry can recover
        // it. Mirrors Vue's `authStore.logout()` call at
        // `rw.axios.ts:125`: log the user out and redirect to this app's
        // own login page.
        logger.error('Refresh token rejected (401); logging out and redirecting to login');
        await forceLogoutToLogin();
      } else if (status === HTTP_FORBIDDEN) {
        logger.warn('Refresh forbidden (403); session retained');
      } else if (status === HTTP_INTERNAL_SERVER_ERROR) {
        logger.error('Refresh 500:', error.response.statusText);
      } else {
        logger.error(
          'Refresh failed:',
          status,
          error.response.statusText,
        );
      }
    } else if (axios.isAxiosError(error) && error.request) {
      // The request was made but no response was received — network
      // error, server down, timeout. Matches Vue rw.axios.ts:145-149.
      logger.error('Refresh network error:', error.message);
    } else {
      logger.error('Refresh error:', error);
    }
    throw error;
  }
}

apiClient.interceptors.response.use(
  (response) => {
    // Learn the server's clock from its own `Date` header. The Marketing cache
    // is invalidated at a wall-clock boundary (08:00 America/Chicago) and that
    // rule has to hold on a machine whose clock is wrong, so the boundary is
    // computed against this rather than against `Date.now()`. Cheap, header-only,
    // and ignored when absent or implausible — see `lib/serverClock.ts`.
    recordServerDate(response.headers?.date);
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableRequestConfig | undefined;
    if (!originalRequest) {
      return Promise.reject(error);
    }

    // Vue rw.axios.ts:87 — any failure on an `/auth/*` URL is rejected
    // immediately. Refreshing the token doesn't fix a failed login or
    // a failed refresh; trying would loop.
    const requestUrl = originalRequest.url ?? '';
    if (requestUrl.includes(AUTH_URL_FRAGMENT)) {
      return Promise.reject(error);
    }

    // CANCELLATION SHORT-CIRCUIT.
    //
    // `axios.isCancel(error)` / `error.code === 'ERR_CANCELED'` catches
    // requests aborted via `AbortController` — which is exactly what
    // react-query does on every unmount, refetch, and key change. Those
    // errors have NO `.response`, so without this guard they'd fall
    // into the no-response branch below and spuriously trigger a
    // refresh on a perfectly healthy session. (The symptom: every
    // visible request returns 200, yet `/auth/refreshtoken` is still
    // being hit — because a CANCELLED sibling request fired the
    // refresh.)
    //
    // Vue's mirror doesn't show this because pinia doesn't auto-cancel
    // the way react-query does; the code path is technically the same
    // but never reached in the Vue app.
    if (
      axios.isCancel(error)
      || (axios.isAxiosError(error) && error.code === 'ERR_CANCELED')
    ) {
      return Promise.reject(error);
    }

    // Vue rw.axios.ts:90-93 — trigger refresh on 401 OR on an axios
    // error with no `response` object ("In some case response object
    // is comming as undefined, when it is thorwing 401" — Vue comment).
    // The `_retry` guard prevents the retried request from triggering
    // another refresh when it itself returns 401.
    const status = error.response?.status;
    const isNetworkLike401 = axios.isAxiosError(error) && !error.response;
    const shouldRefresh =
      !originalRequest._retry
      && (status === HTTP_UNAUTHORIZED || isNetworkLike401);

    if (!shouldRefresh) {
      // Vue rw.axios.ts:100-105 — file an Asana bug ticket (internal staff
      // only, fire-and-forget) for any client/server error that isn't a
      // 401 and wasn't explicitly opted out via SKIP_ANALYTICS_HEADER
      // (`reportBug.api.ts`'s own Asana POST sets this to avoid recursing
      // into itself). `status` above already holds `error.response?.status`.
      if (status && status >= HTTP_CLIENT_ERROR_THRESHOLD && status !== HTTP_UNAUTHORIZED) {
        reportApiFailureToAnalytics(error);
      }
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // Coalesce concurrent 401s onto one refresh call.
    inFlightRefresh ??= refreshAccessToken().finally(() => {
      inFlightRefresh = null;
    });

    try {
      await inFlightRefresh;
    } catch (refreshError) {
      // Refresh failed. `refreshAccessToken` already triggered any
      // side-effects (logout on 401). Propagate so the original caller
      // sees the rejection; no extra logout firing here.
      throw refreshError instanceof Error
        ? refreshError
        : new Error(String(refreshError));
    }

    // Refresh succeeded → replay the original request. The request
    // interceptor will attach the new access token from localStorage.
    // Any failure on this replay (network, 5xx, even another 401)
    // propagates as a normal rejection — `_retry` is already true so
    // the response interceptor won't re-enter refresh for this
    // request. This is the key fix vs. the previous implementation,
    // which treated replay failures as refresh failures and could
    // (a) fire `goToVueLogin()` on a transient retry blip, and
    // (b) trigger another refresh round on the next batch of API
    //     calls, producing the "continuous refresh" symptom.
    return apiClient(originalRequest);
  },
);

export default apiClient;
